import React, { useEffect, useState } from "react";
import { AutoSuggest } from "@rio-cloud/rio-uikit";
import { AutoSuggestSuggestion, SelectedSuggestion } from "@rio-cloud/rio-uikit/components/autosuggest/AutoSuggest";
import { formatValueKmMask } from "@utils";

type TProps = {
  onSuggestionSelected?: (
    event: React.MouseEvent<HTMLLIElement> | React.KeyboardEvent<HTMLDivElement>,
    selectedSuggestion: SelectedSuggestion
  ) => void;
  placeholder: string;
  icon?: string;
  valueProp?: string;
  fetchData: (text: string) => Promise<any>;
  defaultValue?: string;
  defaultDescription?: string;
  defaultPosition?: { lat: number; lng: number };
  onClear?: () => void;
  hasError?: boolean;
  clearValue?: boolean;
  showClearAlways?: boolean;
};

const Autosuggest = ({
  onSuggestionSelected,
  placeholder,
  fetchData,
  icon,
  defaultValue,
  defaultDescription,
  onClear,
  hasError,
  clearValue,
  defaultPosition,
  showClearAlways,
}: TProps) => {
  const [value, setValue] = useState(defaultValue || "");
  const [key] = useState(Math.random());
  const [suggestions, setSuggestions] = useState<AutoSuggestSuggestion[]>([]);

  useEffect(() => {
    if (defaultValue) setValue(defaultValue);
  }, [defaultValue]);

  const handleSuggestionsFetchRequested = async (result: { value: string }) => {
    try {
      const newValue = result.value;
      setValue(newValue);
      if (newValue) {
        const response = await fetchData(newValue);
        setSuggestions(response);
      }
    } catch (error) {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    if (clearValue) {
      setValue("");
    }
  }, [clearValue]);

  const renderSuggestion = (suggestion: AutoSuggestSuggestion) => {
    if (!suggestion?.position) return null;
    let isSelected = false;
    if (!defaultDescription) {
      isSelected =
        defaultPosition?.lat === (suggestion as any)?.position.lat &&
        defaultPosition?.lng === (suggestion as any)?.position.lng;
    } else {
      const valueToCompare = defaultDescription
        ? defaultDescription?.toString().toLowerCase()
        : defaultValue?.toString().toLowerCase();
      isSelected =
        typeof suggestion.title === "string" &&
        (suggestion as any)?.address?.label?.toLowerCase().includes(valueToCompare);
    }

    return (
      <div className={`display-flex align-items-center ${isSelected ? "text-color-primary" : ""}`}>
        <div>
          <span className="rioglyph rioglyph-poi text-size-20"></span>
        </div>
        <div className="display-flex align-items-center width-100pct">
          <div className="width-100pct padding-left-20 padding-right-20">
            <div>
              <span className="text-bold text-size-16">{suggestion.title?.toString()?.substring(0, 48)}</span>
            </div>
            <div className="width-100pct olverflow-hidden">
              <span>{suggestion?.description}</span>
            </div>
          </div>
          <div className="width-10pct">
            <span className="text-color-gray">{formatValueKmMask(suggestion?.distance as number)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`width-100pct margin-0 padding-0 form-group ${hasError ? "has-error" : ""}`}>
      {showClearAlways ? (
        <div className="position-relative">
          <AutoSuggest
            key={key}
            className="width-100pct"
            inputProps={{
              placeholder,
              value,
              icon,
              className: "width-100pct",
              hasError,
              onClear: undefined, // Desabilita o clear padrão
            }}
            suggestions={suggestions}
            onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
            renderSuggestion={renderSuggestion}
            closeOnSelect={true}
            onSuggestionSelected={onSuggestionSelected}
          />
          <button
            type="button"
            className="position-absolute"
            style={{
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1000,
              background: "transparent",
              border: "none",
              padding: "4px",
              cursor: "pointer",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={onClear}
            title="Remover parada"
          >
            <span
              className="rioglyph rioglyph-remove"
              style={{
                fontSize: "12px",
                color: "#999",
                lineHeight: 1,
              }}
            ></span>
          </button>
        </div>
      ) : (
        <AutoSuggest
          key={key}
          className="width-100pct"
          inputProps={{
            placeholder,
            value,
            icon,
            className: "width-100pct",
            hasError,
            onClear,
          }}
          suggestions={suggestions}
          onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
          renderSuggestion={renderSuggestion}
          closeOnSelect={true}
          onSuggestionSelected={onSuggestionSelected}
        />
      )}
    </div>
  );
};

export default Autosuggest;
